/**
 * Options for `ENTITY_REF` form fields.
 *
 * A schema field of type `ENTITY_REF` names a `refEntityType` ("client", "employee",
 * "legal_entity"…) and nothing else — no route, no label field, no permission. Before this
 * hook the renderer fell through to a plain text input, so the only way to complete a
 * project's Client field was to paste a ULID. `/api/platform/entity-refs/:entityType`
 * resolves the type to `{ id, label, sublabel }` rows and this hook feeds them to the picker.
 *
 * Cached for five minutes: reference lists change far less often than they are read, but
 * not so rarely that a client created ninety seconds ago should be missing from the picker.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';

export interface EntityRefOption {
  id: string;
  label: string;
  sublabel: string | null;
  isActive: boolean;
}

export interface EntityRefResponse {
  entityType: string;
  items: EntityRefOption[];
  total: number;
  /** Why the list is empty, in words the user can act on. `null` when it is not. */
  emptyReason: string | null;
}

export function useEntityRefs(
  entityType: string | null,
  options: { search?: string; enabled?: boolean } = {},
): UseQueryResult<EntityRefResponse> {
  const search = options.search ?? '';
  return useQuery({
    queryKey: ['platform', 'entity-refs', entityType, search],
    queryFn: () =>
      api.get<EntityRefResponse>(`/platform/entity-refs/${entityType ?? ''}`, {
        search: search === '' ? undefined : search,
      }),
    enabled: (options.enabled ?? true) && entityType !== null && entityType !== '',
    staleTime: 5 * 60_000,
    // A picker that cannot load must say so once, not retry three times behind a spinner.
    retry: false,
  });
}
